import { useState } from "react";

// ── helpers ──────────────────────────────────────────────────────────────────

function basename(path) {
  return (path || "").split("/").pop() || path;
}

function langLabel(code) {
  if (!code || code === "unknown") return null;
  try {
    return new Intl.DisplayNames(["en"], { type: "language" }).of(code);
  } catch {
    return code.toUpperCase();
  }
}

// ── copy-to-clipboard button ─────────────────────────────────────────────────

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard not available in some contexts */
    }
  };

  return (
    <button
      onClick={handleCopy}
      title="Copy transcript"
      className="
        flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
        bg-[#2e2e2e] hover:bg-[#3a3a3a] text-[#aaa] hover:text-white
        transition-colors shrink-0
      "
    >
      {copied ? (
        <>
          <svg className="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-green-400">Copied</span>
        </>
      ) : (
        <>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <rect x="9" y="9" width="13" height="13" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
          </svg>
          Copy
        </>
      )}
    </button>
  );
}

// ── single transcript card ───────────────────────────────────────────────────

function TranscriptCard({ transcript, index, total }) {
  const [expanded, setExpanded] = useState(true);   // open by default

  const filename = basename(transcript.file);
  const lang     = langLabel(transcript.language);
  const text     = transcript.transcript || "";
  const hasText  = text.trim().length > 0;

  // Word count
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-2xl overflow-hidden">

      {/* ── Header row ──────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-5 py-4 cursor-pointer select-none hover:bg-[#1e1e1e] transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Video icon */}
        <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-500/15 shrink-0">
          <svg className="w-4 h-4 text-indigo-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </span>

        {/* Title */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">
            {total > 1 ? `Video ${index + 1} — ` : ""}{filename}
          </p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {lang && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300">
                {lang}
              </span>
            )}
            {hasText && (
              <span className="text-xs text-[#555]">{wordCount.toLocaleString()} words</span>
            )}
            {transcript.error && (
              <span className="text-xs text-red-400">{transcript.error}</span>
            )}
          </div>
        </div>

        {/* Copy + collapse toggle */}
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {hasText && <CopyButton text={text} />}
        </div>
        <svg
          className={`w-4 h-4 text-[#555] shrink-0 transition-transform duration-200 ${expanded ? "" : "-rotate-90"}`}
          fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </div>

      {/* ── Body ────────────────────────────────────────────────────── */}
      {expanded && (
        <div className="border-t border-[#2e2e2e]">
          {!hasText ? (
            <div className="px-5 py-6 text-center">
              <p className="text-sm text-[#555]">
                {transcript.error
                  ? `Transcription failed: ${transcript.error}`
                  : "No speech detected in this video."}
              </p>
            </div>
          ) : (
            <div className="px-5 py-5">
              {/* Full plain-text transcript */}
              <p className="text-sm text-gray-300 leading-7 whitespace-pre-wrap font-[system-ui] tracking-wide">
                {text}
              </p>

              {/* File paths */}
              <div className="mt-5 pt-4 border-t border-[#2e2e2e] flex flex-wrap gap-3">
                {transcript.txt_path && (
                  <FileBadge label=".txt" path={transcript.txt_path} color="emerald" />
                )}
                {transcript.srt_path && (
                  <FileBadge label=".srt" path={transcript.srt_path} color="sky" />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FileBadge({ label, path, color }) {
  const cls = color === "emerald"
    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
    : "bg-sky-500/10 text-sky-400 border-sky-500/20";
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-mono ${cls}`}>
      <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
      </svg>
      <span className="truncate max-w-[220px]" title={path}>
        {label} saved → {path.split("/").slice(-2).join("/")}
      </span>
    </div>
  );
}

// ── public component ─────────────────────────────────────────────────────────

export default function TranscriptSection({ transcripts }) {
  // Only render cards that attempted transcription (have a file key)
  const valid = (transcripts || []).filter((t) => t.file);
  if (valid.length === 0) return null;

  const anySuccess = valid.some((t) => t.success && t.transcript);

  return (
    <section className="space-y-4">
      {/* Section heading */}
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold">
          Transcripts
          <span className="ml-2 text-sm font-normal text-[#555]">
            ({valid.length} video{valid.length !== 1 ? "s" : ""})
          </span>
        </h2>
        {!anySuccess && (
          <span className="text-xs px-2.5 py-1 rounded-full bg-yellow-500/15 text-yellow-400">
            No speech detected
          </span>
        )}
      </div>

      {/* One card per video */}
      <div className="space-y-4">
        {valid.map((t, i) => (
          <TranscriptCard
            key={t.file || i}
            transcript={t}
            index={i}
            total={valid.length}
          />
        ))}
      </div>
    </section>
  );
}
